import { useQuery } from '@tanstack/react-query';
import { __ } from '../../../i18n/l10n';
import useJwt from '../../utils/useJwt';
import useLocaleQueryFilter from '../../utils/useLocaleQueryFilter';
import { AttributeValue } from './useAttributes';
import { OrderCardType } from './useOrders';
import { ImageType } from './useProducts';

export enum ProviderOrderStatuses {
  '010-PENDING' = '010-PENDING',

  '110-ORDERED' = '110-ORDERED',
  '120-PROBLEM' = '120-PROBLEM',

  '210-PROCESSED' = '210-PROCESSED',
  '280-RETRIED' = '280-RETRIED',
  '290-CANCELED' = '290-CANCELED',
}

export enum OrderItemStatuses {
  '011-NOT_TRANSMITTED' = '011-NOT_TRANSMITTED',

  '111-PREPARATION' = '111-PREPARATION',
  '112-PICKED_UP' = '112-PICKED_UP',
  '113-TRANSIT' = '113-TRANSIT',
  '114-ON_THE_WAY_FOR_DELIVERY' = '114-ON_THE_WAY_FOR_DELIVERY',

  '121-WAITING_FOR_CUSTOMER_RESPONSE' = '121-WAITING_FOR_CUSTOMER_RESPONSE',
  '122-SOLVING_PROBLEM' = '122-SOLVING_PROBLEM',
  '129-INCIDENT' = '129-INCIDENT',

  '211-DELIVERED' = '211-DELIVERED',
  '212-RETURNED' = '212-RETURNED',

  '281-CANCELED_FOR_RETRY' = '281-CANCELED_FOR_RETRY',
  '291-CANCELED_BY_PROVIDER' = '291-CANCELED_BY_PROVIDER',
  '292-CANCELED_BY_ADMIN' = '292-CANCELED_BY_ADMIN',
  '293-REFUNDED' = '293-REFUNDED',
}

export const providerOrderStatusesLabels: {
  [key in ProviderOrderStatuses]: string;
} = {
  [ProviderOrderStatuses['010-PENDING']]: __('Order ready for delivery'),
  [ProviderOrderStatuses['110-ORDERED']]: __('Order being prepared'),
  [ProviderOrderStatuses['120-PROBLEM']]: __('Order being processed manually'),
  [ProviderOrderStatuses['210-PROCESSED']]: __('Order completed'),
  [ProviderOrderStatuses['280-RETRIED']]: __('Order resubmitted'),
  [ProviderOrderStatuses['290-CANCELED']]: __('Order canceled'),
};

export type OrderItemType = {
  id: number;
  localizedProductImage?: ImageType;
  defaultProductImage?: ImageType;
  localizedVariantImage?: ImageType;
  defaultVariantImage?: ImageType;
  label: string;
  status: string;
  slug: string;
  quantity: number;
  totalMilesAmount: number;
  attributes?: AttributeValue[];
};

type ProviderOrder = {
  id: number;
  orderItems: OrderItemType[];
  status: ProviderOrderStatuses;
  trackingNumber: string;
  trackingUrl: string;
};

type OrderType = {
  providerOrders: ProviderOrder[];
} & Omit<OrderCardType, 'image'>;

export default function useOrder(orderId: number) {
  const jwt = useJwt();
  const filter = useLocaleQueryFilter({ orderId, jwt });
  return useQuery<OrderType, Error>(['orders/one', filter], {
    enabled: !!jwt,
  });
}

export const debitMilesErrors: any = {
  E_TRANSACTION_LOCKED: __('an identical order in progress'),
  E_MILES_INTERNAL_ERROR: __('a technical error'),
  E_RECIPIENT_WITHOUT_BONUS: __('a technical error'),
  E_INCONSISTANT_MILES_AMOUNT: __('a technical error'),
  E_RECIPIENT_NOT_FOUND: __('an error in the recipient of the bonus'),
  E_RECIPIENT_INVALID_STATUS: __('an error in the recipient of the bonus'),
  E_RECIPIENT_INVALID_ARJEL_STATUS: __(
    'an error in the recipient of the bonus',
  ),
  E_RECIPIENT_GIFT_TO_SELF: __('an error in the recipient of the bonus'),
  E_POKER_BONUS_TO_DE: __('an error in the recipient of the bonus'),
  E_RECIPIENT_DGOJ_FORBIDDEN: __('an error in the recipient of the bonus'),
  E_TRANSACTION_ALREADY_PROCESSED: __('a technical error'),
  E_UNABLE_TO_GET_MILES_INFOS: __('a technical error'),
  E_INSUFFICIENT_MILES_RESERVE: __('an insufficient Miles balance'),
  E_FAILED_TO_ENQUEUE: __('a technical error'),
  E_DEPOSIT_MODERATORS: __('a deposit limit reached on your account'),
  E_ADDICT_OR_RMJ30_RECIPIENT: true,
};

export const debitMilesCompleteErrors: any = {
  E_ADDICT_OR_RMJ30_RECIPIENT: __(
    'You cannot offer this product to this player',
  ),
};
